import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { sentenceCaseHeading } from "../../lib/soap-note-sections";
import { DRAWER_TEXT, NOTE_BLOCK } from "./drawer-type-roles";

/** Shouted headings read as sentence case; anything richer than text is left alone. */
function headingContent(children: React.ReactNode): React.ReactNode {
  const only =
    Array.isArray(children) && children.length === 1 ? children[0] : children;

  return typeof only === "string" ? sentenceCaseHeading(only) : children;
}

const components: Components = {
  h1: ({ children }) => (
    <h1 className={`${DRAWER_TEXT.sectionHeading} ${NOTE_BLOCK.sectionHeading}`}>
      {headingContent(children)}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className={`${DRAWER_TEXT.sectionHeading} ${NOTE_BLOCK.sectionHeading}`}>
      {headingContent(children)}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className={`${DRAWER_TEXT.subheading} ${NOTE_BLOCK.subheading}`}>
      {headingContent(children)}
    </h3>
  ),
  p: ({ children }) => (
    <p
      className={`${NOTE_BLOCK.paragraph} ${NOTE_BLOCK.groupLabel} ${NOTE_BLOCK.groupLabelAfterList} whitespace-pre-line`}
    >
      {children}
    </p>
  ),
  strong: ({ children }) => <strong className="font-medium">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className={`${NOTE_BLOCK.list} ${NOTE_BLOCK.listItems} list-disc`}>
      {children}
    </ul>
  ),
  ol: ({ children, start }) => (
    <ol
      start={start}
      className={`${NOTE_BLOCK.list} ${NOTE_BLOCK.listItems} list-decimal`}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="whitespace-pre-line [&_p]:mt-0">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className={`${NOTE_BLOCK.quote} ${DRAWER_TEXT.quote} [&_p]:mt-0`}>
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  a: ({ children }) => <span>{children}</span>,
  img: ({ alt }) => <span>{alt}</span>,
};

/** ReactMarkdown escapes raw HTML; never add rehypeRaw or forward link/image attributes. */
export function PriorSoapNoteMarkdown({
  markdown,
}: {
  markdown: string;
}): React.ReactElement {
  return (
    <div className={`break-words ${DRAWER_TEXT.body}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
